/**
 * MenuButton - Hamburger menu with bottom sheet navigation
 *
 * Composed component: Depends on BottomSheet from core.
 * Used for: Admin navigation menu
 */

import { useState } from 'react';
import { Menu, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BottomSheet } from '../core/BottomSheet';

export interface MenuOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

export interface MenuButtonProps {
  /** Available menu options */
  options: MenuOption[];
  /** Currently active/selected value */
  value?: string;
  /** Callback when option is selected */
  onSelect: (value: string) => void;
  /** Bottom sheet title */
  sheetTitle?: string;
  /** Disabled state */
  disabled?: boolean;
}

export function MenuButton({
  options,
  value,
  onSelect,
  sheetTitle = 'Menu',
  disabled = false,
}: MenuButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (optionValue: string) => {
    onSelect(optionValue);
    setIsOpen(false);
  };

  return (
    <>
      {/* Hamburger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={cn(
          'p-3 rounded-full bg-white flex-shrink-0',
          'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
          'text-slate-600 active:bg-slate-50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-sky-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-label={sheetTitle}
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Bottom sheet */}
      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={sheetTitle}
      >
        <div className="space-y-1">
          {options.map((option) => {
            const isSelected = option.value === value;
            const OptionIcon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-4 rounded-xl',
                  'text-left text-base transition-colors',
                  'active:bg-slate-100',
                  isSelected
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-slate-800 hover:bg-slate-50'
                )}
              >
                {OptionIcon && (
                  <OptionIcon className={cn(
                    'h-5 w-5',
                    isSelected ? 'text-sky-500' : 'text-slate-600'
                  )} />
                )}
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
